import React, { useState, useEffect } from 'react';
import { Box, Typography, Select, MenuItem, FormControl, InputLabel, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Checkbox, Button, CircularProgress, Stack, Chip, Avatar } from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import SecurityIcon from '@mui/icons-material/Security';
import axios from 'axios';
import API_URL from '../config';

const PAGES = ['Dashboard', 'Projects', 'Contracts', 'Hierarchy', 'KPIs', 'Client Master', 'Leads', 'References', 'Task List', 'Punching', 'ACTS ChatBot', 'IT Dashboard', 'My Tasks', 'IT Projects', 'IT Suppliers', 'Software Licenses', 'My Tickets', 'Tickets', 'Inbox', 'Setup'];

function RolePermissions() {
  const [roles, setRoles] = useState([]);
  const [selectedRole, setSelectedRole] = useState('');
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    axios.get(`${API_URL}/setup/roles`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } })
      .then(res => setRoles(res.data))
      .catch(err => console.error(err));
  }, []);

  useEffect(() => {
    if (selectedRole) {
      setLoading(true);
      axios.get(`${API_URL}/setup/roles/${selectedRole}/permissions`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } })
        .then(res => {
          const fetched = res.data;
          const mapped = PAGES.map(page => {
            const found = fetched.find(p => p.page_name === page);
            if (found) return found;
            return { page_name: page, can_read: false, can_write: false, can_edit: false };
          });
          setPermissions(mapped);
          setLoading(false);
        })
        .catch(err => { console.error(err); setLoading(false); });
    }
  }, [selectedRole]);

  const handleToggle = (index, field) => {
    const newPerms = [...permissions];
    newPerms[index][field] = !newPerms[index][field];
    setPermissions(newPerms);
  };

  const handleSave = () => {
    axios.put(`${API_URL}/setup/roles/${selectedRole}/permissions`, permissions, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } })
      .then(() => alert('Permissions saved successfully!'))
      .catch(err => alert('Error saving permissions'));
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar variant="rounded" sx={{ bgcolor: '#fef3c7', color: '#f59e0b', width: 48, height: 48, borderRadius: 2 }}>
                <SecurityIcon />
            </Avatar>
            <Box>
                <Typography variant="h6" sx={{ fontWeight: 800, color: '#1e293b' }}>Manage Role Permissions</Typography>
                <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600 }}>Configure access levels for each role</Typography>
            </Box>
        </Box>
        {selectedRole && !loading && (
          <Button 
              variant='contained' 
              onClick={handleSave}
              startIcon={<SaveIcon />}
              sx={{ 
                  bgcolor: '#1e3c72', color: 'white', borderRadius: 2, px: 3, fontWeight: 'bold',
                  boxShadow: '0 4px 14px rgba(30, 60, 114, 0.2)',
                  '&:hover': { bgcolor: '#152b53', transform: 'translateY(-1px)' },
                  transition: 'all 0.2s'
              }}
          >
            Save Permissions
          </Button>
        )}
      </Box>

      <Box sx={{ border: '1px solid #e2e8f0', borderRadius: 3, overflow: 'hidden', boxShadow: '0 4px 20px rgba(0,0,0,0.02)', bgcolor: 'white', p: 3, mb: 3 }}>
        <Stack direction="row" alignItems="center" spacing={2}>
            <Typography sx={{ fontWeight: 600, color: '#475569' }}>Select Role to Edit:</Typography>
            <FormControl sx={{ minWidth: 300 }}>
                <Select 
                    value={selectedRole} 
                    onChange={(e) => setSelectedRole(e.target.value)}
                    displayEmpty
                    size="small"
                    sx={{ 
                        borderRadius: 2, 
                        bgcolor: '#f8fafc',
                        '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e2e8f0' },
                        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#cbd5e1' },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#3b82f6' }
                    }}
                >
                    <MenuItem value="" disabled><em>Select a Role</em></MenuItem>
                    {roles.map(r => <MenuItem key={r.id} value={r.id} sx={{ fontWeight: 500 }}>{r.name}</MenuItem>)}
                </Select>
            </FormControl>
        </Stack>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 8 }}>
          <CircularProgress sx={{ color: '#3b82f6' }} />
        </Box>
      ) : selectedRole ? (
        <Box sx={{ border: '1px solid #e2e8f0', borderRadius: 3, overflow: 'hidden', boxShadow: '0 4px 20px rgba(0,0,0,0.02)' }}>
          <TableContainer sx={{ bgcolor: 'white' }}>
            <Table size="medium">
              <TableHead sx={{ bgcolor: '#f8fafc' }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, color: '#64748b', fontSize: '0.75rem' }}>PAGE NAME</TableCell>
                  <TableCell align='center' sx={{ fontWeight: 700, color: '#64748b', fontSize: '0.75rem' }}>CAN READ</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {permissions.map((p, i) => (
                  <TableRow key={p.page_name} hover sx={{ '& td': { borderBottom: '1px solid #f1f5f9' }, transition: 'background-color 0.2s' }}>
                    <TableCell sx={{ fontWeight: 600, color: '#1e293b' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: p.can_read ? '#10b981' : '#cbd5e1' }} />
                            {p.page_name}
                        </Box>
                    </TableCell>
                    <TableCell align='center'>
                        <Checkbox 
                            checked={p.can_read} 
                            onChange={() => handleToggle(i, 'can_read')} 
                            sx={{ 
                                color: '#cbd5e1', 
                                '&.Mui-checked': { color: '#3b82f6' },
                                '& .MuiSvgIcon-root': { fontSize: 24 }
                            }} 
                        />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', p: 8, border: '1px dashed #e2e8f0', borderRadius: 3, bgcolor: '#f8fafc' }}>
            <SecurityIcon sx={{ fontSize: 64, color: '#cbd5e1', mb: 2 }} />
            <Typography variant="h6" sx={{ color: '#64748b', fontWeight: 600 }}>No Role Selected</Typography>
            <Typography variant="body2" sx={{ color: '#94a3b8' }}>Please select a role from the dropdown above to manage its permissions.</Typography>
        </Box>
      )}
    </Box>
  );
}
export default RolePermissions;
